// @ts-nocheck
// Shared helpers para todas as telas de Clubes/Grupos.
import React from 'react';
import { Icon } from '../icons';
import { Flame } from '../common';
import { PostReactions } from './data';

export type Term = {
  s: string; S: string;
  p: string; P: string;
  poss: string;
  nav: string;
};

export function useTerm(termSingular: 'clube' | 'grupo' = 'clube'): Term {
  return termSingular === 'grupo'
    ? { s: 'grupo', S: 'Grupo', p: 'grupos', P: 'Grupos', poss: 'do grupo', nav: 'GRUPOS' }
    : { s: 'clube', S: 'Clube', p: 'clubes', P: 'Clubes', poss: 'do clube', nav: 'CLUBES' };
}

// Banner abstrato pra clubes sem capa de livro
export const ClubBanner: React.FC<{
  hue?: number; style?: string; height?: number; label?: React.ReactNode; coverUrl?: string | null; bannerUrl?: string | null; ratio?: number;
}> = ({ hue = 22, style = 'paper', height = 140, label, coverUrl, bannerUrl, ratio }) => {
  const imgUrl = bannerUrl || coverUrl || null;
  const bg1 = `oklch(0.78 0.09 ${hue})`;
  const bg2 = `oklch(0.42 0.10 ${hue})`;
  const stripe = `oklch(0.55 0.08 ${hue})`;
  return (
    <div style={{
      width: '100%', ...(ratio ? { aspectRatio: String(ratio) } : { height }), position: 'relative', overflow: 'hidden',
      background: `linear-gradient(155deg, ${bg1} 0%, ${bg2} 100%)`,
    }}>
      {imgUrl && <img src={imgUrl} alt="" style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover' }}/>}
      {!imgUrl && <div style={{
        position: 'absolute', inset: 0, opacity: 0.18,
        backgroundImage: `repeating-linear-gradient(45deg, ${stripe}55 0 1px, transparent 1px 6px)`,
      }}/>}
      {!imgUrl && <svg viewBox="0 0 400 200" preserveAspectRatio="none" style={{ position: 'absolute', inset: 0, opacity: 0.16 }}>
        <circle cx="320" cy="60" r="120" fill="none" stroke="white" strokeWidth="1"/>
        <circle cx="320" cy="60" r="80" fill="none" stroke="white" strokeWidth="1"/>
      </svg>}
      <div style={{
        position: 'absolute', inset: 0,
        background: 'linear-gradient(180deg, rgba(0,0,0,0) 40%, rgba(0,0,0,0.45) 100%)',
      }}/>
      {label}
    </div>
  );
};

export const VerifiedSeal: React.FC<{ role?: string; size?: 'sm' | 'md' }> = ({ role = 'Pastor verificado', size = 'sm' }) => (
  <div style={{
    display: 'inline-flex', alignItems: 'center', gap: 5,
    padding: size === 'sm' ? '3px 7px' : '4px 10px',
    borderRadius: 999,
    background: 'var(--ember-soft)', color: 'var(--ember)',
    fontFamily: 'Geist Mono, monospace',
    fontSize: size === 'sm' ? 9 : 10,
    letterSpacing: '0.14em', textTransform: 'uppercase', fontWeight: 600,
  }}>
    <svg width="9" height="9" viewBox="0 0 24 24" fill="currentColor">
      <path d="M12 2l2.4 1.8 3 .2.6 3 2 2.2-1.4 2.7.4 3-2.7 1.4-1.4 2.7-3-.4-2.7 1.4-2.2-2-3-.6-.2-3L2 12l1.8-2.4 .2-3 3-.6 2.2-2L12 2z"/>
      <path d="M9 12l2 2 4-4" fill="none" stroke="white" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
    {role}
  </div>
);

export const LivePulse: React.FC<{ count: number; label?: string }> = ({ count, label = 'lendo agora' }) => (
  <div style={{ display: 'inline-flex', alignItems: 'center', gap: 7 }}>
    <span style={{ position: 'relative', width: 8, height: 8 }}>
      <span style={{
        position: 'absolute', inset: 0, borderRadius: '50%', background: 'var(--ember)',
        animation: 'clubPulse 2s ease-out infinite',
      }}/>
      <span style={{ position: 'absolute', inset: 0, borderRadius: '50%', background: 'var(--ember)' }}/>
    </span>
    <span className="mono" style={{ fontSize: 10.5, letterSpacing: '0.08em', color: 'var(--ink-2)' }}>
      <strong style={{ color: 'var(--ember)', fontWeight: 600 }}>{count}</strong> {label}
    </span>
    <style>{`
      @keyframes clubPulse {
        0% { transform: scale(1); opacity: 0.7; }
        100% { transform: scale(2.6); opacity: 0; }
      }
    `}</style>
  </div>
);

export const Avatar: React.FC<{ letter?: string; size?: number; hue?: number; ring?: boolean }> = ({
  letter = 'M', size = 32, hue = 22, ring = false,
}) => {
  const bg1 = `oklch(0.65 0.10 ${hue})`;
  const bg2 = `oklch(0.45 0.12 ${(hue + 30) % 360})`;
  return (
    <div style={{
      width: size, height: size, borderRadius: '50%',
      background: `linear-gradient(135deg, ${bg1}, ${bg2})`,
      color: 'white',
      display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
      fontFamily: 'Instrument Serif, serif', fontStyle: 'italic',
      fontSize: size * 0.46, flexShrink: 0,
      boxShadow: ring ? '0 0 0 2px var(--paper), 0 0 0 3.5px var(--ember)' : 'none',
    }}>
      {letter}
    </div>
  );
};

export const UsersIcon: React.FC<{ size?: number; color?: string; stroke?: number }> = ({
  size = 20, color = 'currentColor', stroke = 1.6,
}) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none"
    stroke={color} strokeWidth={stroke} strokeLinecap="round" strokeLinejoin="round">
    <circle cx="9" cy="8" r="3.4"/>
    <path d="M3 20a6 6 0 0112 0"/>
    <circle cx="17" cy="9" r="2.6"/>
    <path d="M15.5 14.5a4.5 4.5 0 016 4"/>
  </svg>
);

export const MiniBar: React.FC<{ pct?: number; height?: number; color?: string; track?: string; glow?: boolean }> = ({
  pct = 0, height = 3, color = 'var(--ember)', track = 'var(--paper-3)', glow = false,
}) => (
  <div style={{ height, background: track, borderRadius: height/2, overflow: 'hidden', position: 'relative' }}>
    <div style={{
      position: 'absolute', inset: 0, width: `${pct}%`,
      background: color, borderRadius: height/2,
      transition: 'width 600ms cubic-bezier(.2,.8,.2,1)',
      boxShadow: glow ? '0 0 8px rgba(199,83,26,0.5)' : 'none',
    }}/>
  </div>
);

export const ReactionRow: React.FC<{ r: PostReactions }> = ({ r }) => {
  const items = [
    { key: 'tocou',   emoji: '🙏', label: 'Tocou',       count: r.tocou },
    { key: 'aprendi', emoji: '💡', label: 'Aprendi',     count: r.aprendi },
    { key: 'fogo',    emoji: '🔥', label: 'Que página',  count: r.fogo },
    { key: 'salvar',  emoji: '📌', label: 'Salvar',      count: r.salvar },
  ];
  return (
    <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
      {items.map(it => (
        <button key={it.key} style={{
          display: 'inline-flex', alignItems: 'center', gap: 5,
          padding: '5px 9px', borderRadius: 999,
          background: 'var(--paper)',
          border: '1px solid var(--line)',
          fontSize: 11, color: 'var(--ink-2)',
          fontFamily: 'Geist, sans-serif',
        }}>
          <span style={{ fontSize: 12 }}>{it.emoji}</span>
          <span className="mono" style={{ fontSize: 10, color: 'var(--ink-3)' }}>{it.count}</span>
        </button>
      ))}
    </div>
  );
};

// Tab bar customizada: Hoje | CLUBES/GRUPOS | + | Ritmo | Eu
export const ClubTabBar: React.FC<{
  current?: string; termPlural?: string;
  onChange?: (id: string) => void; onAdd?: () => void;
}> = ({ current = 'clubs', termPlural = 'CLUBES', onChange, onAdd }) => {
  const tabs: any[] = [
    { id: 'home',  label: 'Hoje',      icon: 'home' },
    { id: 'clubs', label: termPlural,  icon: 'users' },
    { id: 'add',   label: '',          icon: 'plus', center: true },
    { id: 'stats', label: 'Ritmo',     icon: 'chart' },
    { id: 'me',    label: 'Eu',        icon: 'user' },
  ];
  return (
    <div className="tabbar">
      {tabs.map(t => {
        if (t.center) {
          return (
            <button key={t.id} className="tab-center" onClick={onAdd}>
              <Icon name="plus" size={22} color="var(--paper)" stroke={2}/>
            </button>
          );
        }
        const active = current === t.id;
        return (
          <button key={t.id} className={`tab ${active ? 'active' : ''}`} onClick={() => onChange && onChange(t.id)}>
            {t.icon === 'users' ? (
              <UsersIcon size={20} color={active ? 'var(--ember)' : 'var(--ink-4)'} stroke={active ? 2 : 1.6}/>
            ) : (
              <Icon name={t.icon} size={20} color={active ? 'var(--ember)' : 'var(--ink-4)'} stroke={active ? 2 : 1.6}/>
            )}
            <span>{t.label}</span>
          </button>
        );
      })}
    </div>
  );
};

// ActionSheet — menu "..." dentro de um clube (admin)
// Inline styles porque as classes CSS do design original (.action-sheet, .row,
// .ic, .sep, .cancel) só existiam no protótipo HTML standalone — não foram
// migradas pro CSS files do app real.

const sheetBgStyle: React.CSSProperties = {
  position: 'fixed',
  inset: 0,
  background: 'rgba(10,8,6,0.55)',
  backdropFilter: 'blur(6px)',
  WebkitBackdropFilter: 'blur(6px)',
  zIndex: 80,
  animation: 'fadeIn 200ms ease-out',
};

const sheetContainerStyle: React.CSSProperties = {
  position: 'fixed',
  left: 12,
  right: 12,
  bottom: 28,
  background: 'var(--paper)',
  borderRadius: 20,
  padding: 8,
  boxShadow: '0 16px 48px rgba(0,0,0,0.3)',
  zIndex: 81,
  animation: 'slideInUp 280ms cubic-bezier(0.22, 1, 0.36, 1)',
  maxWidth: 460,
  margin: '0 auto',
};

const rowStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 14,
  padding: '14px 14px',
  borderRadius: 14,
  color: 'var(--ink)',
  fontSize: 14,
  width: '100%',
  textAlign: 'left',
  background: 'transparent',
  border: 'none',
  cursor: 'pointer',
};

const iconWrapperStyle: React.CSSProperties = {
  width: 30,
  height: 30,
  borderRadius: 8,
  background: 'var(--paper-2)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  color: 'var(--ink-3)',
  flexShrink: 0,
};

const sepStyle: React.CSSProperties = {
  height: 1,
  background: 'var(--line)',
  margin: '4px 12px',
};

const cancelStyle: React.CSSProperties = {
  marginTop: 6,
  width: '100%',
  padding: 14,
  textAlign: 'center',
  color: 'var(--ink-3)',
  fontSize: 13,
  background: 'var(--paper-2)',
  borderRadius: 14,
  border: 'none',
  cursor: 'pointer',
};

export const ActionSheet: React.FC<{
  term: string;
  onClose: () => void;
  onShare: () => void;
  onAnalytics: () => void;
  onQuestionBox: () => void;
  onEditBook?: () => void;
  onMilestones?: () => void;
  /** Edita dados do clube (nome, descrição, privacy, regras). Só pra admin. */
  onEditClub?: () => void;
  /** Sair do clube. Só aparece pra membros não-owner. */
  onLeave?: () => void;
  /** Excluir clube definitivamente. Só pro owner. */
  onDelete?: () => void;
  /** Denuncia o clube — UGC moderation (Apple Guideline 1.2). */
  onReport?: () => void;
  isAdmin?: boolean;
  isOwner?: boolean;
  hasBook?: boolean;
  pendingQuestions?: number;
  activeMilestonesCount?: number;
}> = ({ term, onClose, onShare, onAnalytics, onQuestionBox, onEditBook, onMilestones, onEditClub, onLeave, onDelete, onReport, isAdmin = true, isOwner = false, hasBook = false, pendingQuestions = 0, activeMilestonesCount = 0 }) => (
  <>
    <div onClick={onClose} style={sheetBgStyle}/>
    <div style={sheetContainerStyle}>
      {isAdmin && (
        <button onClick={onAnalytics} style={rowStyle}>
          <span style={iconWrapperStyle}><Icon name="chart" size={16}/></span>
          <div style={{ flex: 1 }}>
            <div>Painel do líder</div>
            <div className="mono" style={{ fontSize: 9.5, color: 'var(--ink-3)', letterSpacing: '0.06em', marginTop: 2 }}>
              analytics & engajamento
            </div>
          </div>
          <Icon name="chevron-right" size={14} color="var(--ink-4)"/>
        </button>
      )}
      {isAdmin && (
        <button onClick={onQuestionBox} style={rowStyle}>
          <span style={{ ...iconWrapperStyle, position: 'relative' }}>
            <Icon name="mail" size={16}/>
            {pendingQuestions > 0 && (
              <span style={{
                position: 'absolute', top: -3, right: -4,
                minWidth: 14, height: 14, padding: '0 3px',
                borderRadius: 7, background: 'var(--ember)',
                color: 'var(--paper)', fontSize: 9, fontWeight: 600,
                display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
              }}>{pendingQuestions}</span>
            )}
          </span>
          <div style={{ flex: 1 }}>
            <div>Caixa de perguntas</div>
            <div className="mono" style={{ fontSize: 9.5, color: 'var(--ink-3)', letterSpacing: '0.06em', marginTop: 2 }}>
              {pendingQuestions === 0
                ? 'gerenciar · responder'
                : `${pendingQuestions} aguardando resposta`}
            </div>
          </div>
          <Icon name="chevron-right" size={14} color="var(--ink-4)"/>
        </button>
      )}
      {isAdmin && hasBook && onEditBook && (
        <button onClick={onEditBook} style={rowStyle}>
          <span style={iconWrapperStyle}><Icon name="edit" size={16}/></span>
          <div style={{ flex: 1 }}>
            <div>Editar livro do {term}</div>
            <div className="mono" style={{ fontSize: 9.5, color: 'var(--ink-3)', letterSpacing: '0.06em', marginTop: 2 }}>
              capa · páginas · página inicial
            </div>
          </div>
          <Icon name="chevron-right" size={14} color="var(--ink-4)"/>
        </button>
      )}
      {isAdmin && onMilestones && (
        <button onClick={onMilestones} style={rowStyle}>
          <span style={iconWrapperStyle}>
            <span style={{ fontSize: 14, lineHeight: 1 }}>✦</span>
          </span>
          <div style={{ flex: 1 }}>
            <div>Marcos do {term}</div>
            <div className="mono" style={{ fontSize: 9.5, color: 'var(--ink-3)', letterSpacing: '0.06em', marginTop: 2 }}>
              {activeMilestonesCount > 0 ? `${activeMilestonesCount} ativo${activeMilestonesCount === 1 ? '' : 's'} · gerenciar` : 'definir página alvo'}
            </div>
          </div>
          <Icon name="chevron-right" size={14} color="var(--ink-4)"/>
        </button>
      )}
      <button onClick={onShare} style={rowStyle}>
        <span style={iconWrapperStyle}><Icon name="share" size={16}/></span>
        <div style={{ flex: 1 }}>
          <div>Convidar mais leitores</div>
          <div className="mono" style={{ fontSize: 9.5, color: 'var(--ink-3)', letterSpacing: '0.06em', marginTop: 2 }}>
            link, QR, stories
          </div>
        </div>
      </button>
      <div style={sepStyle}/>
      {/* Configurações do grupo — admin/owner edita nome, descrição, privacy */}
      {isAdmin && onEditClub && (
        <button onClick={onEditClub} style={rowStyle}>
          <span style={iconWrapperStyle}><Icon name="settings" size={16}/></span>
          <div style={{ flex: 1 }}>
            <div>Configurações do {term}</div>
            <div className="mono" style={{ fontSize: 9.5, color: 'var(--ink-3)', letterSpacing: '0.06em', marginTop: 2 }}>
              nome · descrição · privacidade
            </div>
          </div>
          <Icon name="chevron-right" size={14} color="var(--ink-4)"/>
        </button>
      )}
      {/* Denunciar conteudo — qualquer membro, qualquer hora (Apple Guideline 1.2). */}
      {onReport && (
        <button onClick={onReport} style={rowStyle}>
          <span style={iconWrapperStyle}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
              <path d="M4 21V4M4 4h12l-2 5 2 5H4"/>
            </svg>
          </span>
          <div style={{ flex: 1 }}>Denunciar conteúdo</div>
        </button>
      )}
      {/* Sair do grupo — qualquer membro NÃO-owner. Owner precisa deletar
         o grupo (ou transferir owner antes — fora do escopo). */}
      {!isOwner && onLeave && (
        <button onClick={onLeave} style={{ ...rowStyle, color: '#C03A2A' }}>
          <span style={{ ...iconWrapperStyle, color: '#C03A2A' }}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round">
              <path d="M9 12h12m-4-4l4 4-4 4M14 5V4a2 2 0 00-2-2H5a2 2 0 00-2 2v16a2 2 0 002 2h7a2 2 0 002-2v-1"/>
            </svg>
          </span>
          <div style={{ flex: 1 }}>Sair do {term}</div>
        </button>
      )}
      {/* Excluir grupo — só pro owner. Soft-delete no backend +
         desvincula livros pessoais dos membros. */}
      {isOwner && onDelete && (
        <button onClick={onDelete} style={{ ...rowStyle, color: '#C03A2A' }}>
          <span style={{ ...iconWrapperStyle, color: '#C03A2A' }}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round">
              <path d="M3 6h18M8 6V4a2 2 0 012-2h4a2 2 0 012 2v2m3 0v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6h14z"/>
              <path d="M10 11v6M14 11v6"/>
            </svg>
          </span>
          <div style={{ flex: 1 }}>Excluir {term}</div>
        </button>
      )}
      <button onClick={onClose} style={cancelStyle}>Cancelar</button>
    </div>
  </>
);

// Re-export Flame para conveniência das telas de clubes
export { Flame };
